import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StatusPill, DataTable } from '@ui-kit';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { useBlacklistStatus } from '../hooks/useBlacklistStatus';
import { useActiveSessions } from '../hooks/useActiveSessions';
import {
  jtiLookupFormSchema,
  sessionLookupFormSchema,
  type JtiLookupFormValues,
  type SessionLookupFormValues,
} from '../schema/logout.schema';
import type { ReactNode } from 'react';
import type { ActiveRefreshToken, RevokedJti } from '../types/logout.types';
import type { CommonUsageItem } from '../../_shared/CommonUsagePanel';

// ─── Common Usage Items (§6.7.1 — 16 full rows) ──────────────────────────────

const COMMON_USAGE_ITEMS: CommonUsageItem[] = [
  { module: 'eraf-core', moduleColor: 'blue', api: 'ApiResponse<Void> (C002)', role: '표준 응답 wrapper (logout · inspector 공통)', snippet: 'return ApiResponse.success(null);' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'ErrorResponse (C002)', role: '4xx/5xx 본문 (FE 매핑)', snippet: 'if (!res.success) showToast(res.error.code);' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'BusinessException (C001)', role: '인스펙터 입력 위반 시 throw', snippet: 'throw new BusinessException(ErrorCode.VALIDATION_FAILED);' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'GlobalExceptionHandler (C001)', role: '컨트롤러 advice (자동 적용)', snippet: '// 자동 적용 — biz 코드 없음' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'RequestContextHolder.current() (C006)', role: 'logout 액터의 traceId · userId MDC 조회', snippet: 'Long actor = RequestContextHolder.current().userId();' },
  { module: 'eraf-core', moduleColor: 'blue', api: 'TraceIdFilter (C006)', role: '요청 진입 시 traceId 자동 주입', snippet: '// 자동' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationService.revoke(at, rt) (C028)', role: '표준 logout 핵심 위임', snippet: 'jwtAuthenticationService.revoke(accessToken, refreshToken);' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationService.revokeAllForUser(userId) (C028)', role: '다른 기기 전체 폐기', snippet: 'jwtAuthenticationService.revokeAllForUser(claims.userId());' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationService.verify(accessToken) (C028)', role: '인스펙터 jti 추출', snippet: 'TokenClaims c = jwtAuthenticationService.verify(token);' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtTokenBlacklist.isBlacklisted(jti) (C028)', role: '인스펙터 Redis hit 조회', snippet: 'boolean hit = jwtTokenBlacklist.isBlacklisted(jti);' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtTokenBlacklist.blacklist(jti, exp) (C028)', role: 'revoke 내부 호출 (간접 시연)', snippet: '// 간접 — 자동' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationFilter (C028)', role: '폐기 토큰 재사용 차단 (자동)', snippet: '// 자동 적용 — biz 코드 없음' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationToken · TokenClaims (C028)', role: '@AuthenticationPrincipal jti/userId 식별', snippet: '@AuthenticationPrincipal JwtAuthenticationToken auth' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'JwtAuthenticationEntryPoint (C028)', role: '401 + AUTH-401-004 응답 (재사용)', snippet: '// 자동' },
  { module: 'eraf-security', moduleColor: 'yellow', api: 'SecurityChainCustomizer · PathPolicy.hasRole (C029)', role: '인스펙터 ROLE_ADMIN 제한', snippet: 'PathPolicy.hasRole("/api/eraf/auth/logout/inspector/**", "ADMIN")' },
  { module: 'eraf-security', moduleColor: 'yellow', api: '@HasPermission 또는 ROLE 검사 (C030)', role: '인스펙터 메서드 단위 보호 (옵션)', snippet: '@HasPermission("AUDIT_READ")' },
];

// ─── Pill helpers ─────────────────────────────────────────────────────────────

function SourcePill({ source }: { source: string }) {
  const tone =
    source === 'REDIS-PRIMARY' ? 'success' : source === 'JPA-FALLBACK' ? 'warning' : 'neutral';
  return <StatusPill tone={tone}>{source}</StatusPill>;
}

function ReasonPill({ reason }: { reason: string | null }) {
  if (!reason)
    return (
      <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
        —
      </span>
    );
  const tone = reason === 'ROTATION' ? 'info' : reason === 'LOGOUT' ? 'danger' : 'neutral';
  return <StatusPill tone={tone}>{reason}</StatusPill>;
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function SkeletonRows({ widths }: { widths: number[] }) {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('logout.inspector.loading')}>
      {widths.map((w, i) => (
        <div
          key={i}
          style={{
            height: '14px',
            width: `${w}%`,
            background: 'linear-gradient(90deg, var(--color-surface-elevated) 25%, var(--color-surface-muted) 50%, var(--color-surface-elevated) 75%)',
            backgroundSize: '200% 100%',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 'var(--spacing-2)',
            animation: 'skeleton-shimmer 1.5s infinite',
          }}
        />
      ))}
    </div>
  );
}

// ─── Shared row-result renderer ───────────────────────────────────────────────

function ResultRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-1) 0',
        borderBottom: '1px solid var(--color-surface-border)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          minWidth: '100px',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

// ─── Panel shell ──────────────────────────────────────────────────────────────

function PanelShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-surface-border)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
        }}
      >
        {title}
      </div>
      <div style={{ padding: 'var(--spacing-4)' }}>{children}</div>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--spacing-6)',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        fontSize: 'var(--font-size-sm)',
        background: 'var(--color-surface-elevated)',
        border: '1px dashed var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
        border: '1px solid var(--color-danger)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-danger)',
      }}
    >
      {message}
    </div>
  );
}

// ─── JtiBlacklistPanel ────────────────────────────────────────────────────────

function JtiBlacklistPanel() {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<JtiLookupFormValues>({
    resolver: zodResolver(jtiLookupFormSchema),
  });

  const [submittedJti, setSubmittedJti] = useState('');
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { data, isFetching, isError, error } = useBlacklistStatus(submittedJti, fetchEnabled);

  const onSubmit = useCallback((values: JtiLookupFormValues) => {
    setFetchEnabled(false);
    setSubmittedJti(values.jti);
    // Use requestAnimationFrame to allow state to flush before re-enabling
    requestAnimationFrame(() => setFetchEnabled(true));
  }, []);

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: `1px solid ${errors.jti ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-mono)',
    outline: 'none',
  };

  return (
    <PanelShell title={t('logout.inspector.panel.blacklist')}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
          <input
            type="text"
            placeholder={t('logout.inspector.jtiLookup.placeholder')}
            autoComplete="off"
            aria-label={t('logout.inspector.jtiLookup.placeholder')}
            aria-invalid={!!errors.jti}
            aria-describedby={errors.jti ? 'jti-error' : undefined}
            {...register('jti')}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={isFetching}
            aria-label={t('logout.inspector.jtiLookup.submitAriaLabel')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isFetching ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              whiteSpace: 'nowrap',
              opacity: isFetching ? 0.7 : 1,
            }}
          >
            {isFetching ? t('logout.inspector.querying') : t('logout.inspector.queryButton')}
          </button>
        </div>
        {errors.jti && (
          <span id="jti-error" role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'block', marginBottom: 'var(--spacing-2)' }}>
            {errors.jti.message}
          </span>
        )}
      </form>

      {/* JPA fallback warning */}
      {data?.source === 'JPA-FALLBACK' && (
        <div
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-warning)',
            display: 'flex',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">⚠</span>
          {t('logout.inspector.jpaFallbackWarning')}
        </div>
      )}

      {isFetching && <SkeletonRows widths={[80, 60, 40, 70, 50]} />}

      {isError && !isFetching && (
        <ErrorBanner message={(error as { message?: string })?.message ?? t('logout.inspector.queryFailed')} />
      )}

      {data && !isFetching && (
        <div
          style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-3)' }}
          role="region"
          aria-label={t('logout.inspector.jtiLookup.resultAriaLabel')}
        >
          <ResultRow label="blacklisted">
            <StatusPill tone={data.blacklisted ? 'danger' : 'success'}>
              {data.blacklisted ? t('logout.inspector.blacklisted') : t('logout.inspector.notBlacklisted')}
            </StatusPill>
          </ResultRow>
          <ResultRow label="redisHit">
            <StatusPill tone={data.redisHit ? 'success' : 'danger'}>{data.redisHit ? '✓' : '✗'}</StatusPill>
          </ResultRow>
          <ResultRow label="jpaHit">
            <StatusPill tone={data.jpaHit ? 'success' : 'danger'}>{data.jpaHit ? '✓' : '✗'}</StatusPill>
          </ResultRow>
          <ResultRow label="source">
            <SourcePill source={data.source} />
          </ResultRow>
          <ResultRow label="reason">
            <ReasonPill reason={data.reason} />
          </ResultRow>
          <ResultRow label="blacklistedAt">
            <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {data.blacklistedAt ?? '—'}
            </span>
          </ResultRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-1) 0', fontSize: 'var(--font-size-sm)' }}>
            <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', minWidth: '100px', flexShrink: 0 }}>by</span>
            <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {data.blacklistedBy ?? '—'}
            </span>
          </div>
        </div>
      )}

      {!data && !isFetching && !isError && (
        <EmptyHint>{t('logout.inspector.panel.blacklistEmpty')}</EmptyHint>
      )}
    </PanelShell>
  );
}

// ─── SessionsPanel ────────────────────────────────────────────────────────────

interface SessionsPanelProps {
  onDataReady: (
    data: { revokedItems: RevokedJti[]; activeTokens: ActiveRefreshToken[] } | null
  ) => void;
}

function SessionsPanel({ onDataReady }: SessionsPanelProps) {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<SessionLookupFormValues>({
    resolver: zodResolver(sessionLookupFormSchema),
  });

  const [submittedUserId, setSubmittedUserId] = useState(0);
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { data, isFetching, isError, error } = useActiveSessions(submittedUserId, fetchEnabled);

  useEffect(() => {
    if (data) {
      onDataReady({ revokedItems: data.recentlyRevoked, activeTokens: data.activeRefreshTokens });
    } else {
      onDataReady(null);
    }
  }, [data, onDataReady]);

  const onSubmit = useCallback((values: SessionLookupFormValues) => {
    setFetchEnabled(false);
    setSubmittedUserId(Number(values.userId));
    requestAnimationFrame(() => setFetchEnabled(true));
  }, []);

  const activeColumns = [
    { key: 'jti', header: 'jti', render: (row: ActiveRefreshToken) => <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>{row.jti}</span> },
    { key: 'issuedAt', header: t('logout.inspector.sessions.col.issuedAt'), render: (row: ActiveRefreshToken) => <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{row.issuedAt}</span> },
    { key: 'rotatedFrom', header: t('logout.inspector.sessions.col.rotatedFrom'), render: (row: ActiveRefreshToken) => <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{row.rotatedFrom ?? '—'}</span> },
  ];

  const revokedColumns = [
    { key: 'jti', header: 'jti', render: (row: RevokedJti) => <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>{row.jti}</span> },
    { key: 'reason', header: t('logout.inspector.sessions.col.reason'), render: (row: RevokedJti) => <ReasonPill reason={row.reason} /> },
    { key: 'blacklistedAt', header: t('logout.inspector.sessions.col.blacklistedAt'), render: (row: RevokedJti) => <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{row.blacklistedAt}</span> },
  ];

  return (
    <PanelShell title={t('logout.inspector.panel.sessions')}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder={t('logout.inspector.sessions.placeholder')}
            autoComplete="off"
            aria-label={t('logout.inspector.sessions.placeholder')}
            aria-invalid={!!errors.userId}
            aria-describedby={errors.userId ? 'userId-error' : undefined}
            {...register('userId')}
            style={{
              flex: 1,
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: `1px solid ${errors.userId ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={isFetching}
            aria-label={t('logout.inspector.sessions.submitAriaLabel')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isFetching ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              whiteSpace: 'nowrap',
              opacity: isFetching ? 0.7 : 1,
            }}
          >
            {isFetching ? t('logout.inspector.querying') : t('logout.inspector.queryButton')}
          </button>
        </div>
        {errors.userId && (
          <span id="userId-error" role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'block', marginBottom: 'var(--spacing-2)' }}>
            {errors.userId.message}
          </span>
        )}
      </form>

      {isFetching && <SkeletonRows widths={[100, 80, 60, 90, 70]} />}

      {isError && !isFetching && (
        <ErrorBanner message={(error as { message?: string })?.message ?? t('logout.inspector.queryFailed')} />
      )}

      {data && !isFetching && (
        <div>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
            {data.email} — {t('logout.inspector.sessions.activeCount', { count: data.activeRefreshTokens.length })}
          </div>
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-2)' }}>
              activeRefreshTokens
            </div>
            <DataTable<ActiveRefreshToken>
              columns={activeColumns}
              rows={data.activeRefreshTokens}
              rowKey={(row) => row.jti}
              emptyMessage={t('logout.inspector.sessions.emptyActive')}
            />
          </div>
          <div>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-2)' }}>
              recentlyRevoked
            </div>
            <DataTable<RevokedJti>
              columns={revokedColumns}
              rows={data.recentlyRevoked}
              rowKey={(row) => row.jti}
              emptyMessage={t('logout.inspector.sessions.emptyRevoked')}
            />
          </div>
        </div>
      )}

      {!data && !isFetching && !isError && (
        <EmptyHint>{t('logout.inspector.panel.sessionsEmpty')}</EmptyHint>
      )}
    </PanelShell>
  );
}

// ─── RotationPanel ────────────────────────────────────────────────────────────

interface RotationPanelProps {
  revokedItems: RevokedJti[];
  activeTokens: ActiveRefreshToken[];
}

function RotationPanel({ revokedItems, activeTokens }: RotationPanelProps) {
  const { t } = useTranslation();
  type ChainNode = { jti: string; status: 'revoked' | 'active' };
  const rotationRevoked = revokedItems.filter((r) => r.reason === 'ROTATION');
  const chainNodes: ChainNode[] = [
    ...rotationRevoked.map((r) => ({ jti: r.jti, status: 'revoked' as const })),
    ...activeTokens.map((t) => ({ jti: t.jti, status: 'active' as const })),
  ];

  return (
    <div
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        marginBottom: 'var(--spacing-4)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-4)' }}>
        {t('logout.inspector.panel.rotation')}
      </div>

      {chainNodes.length === 0 ? (
        <EmptyHint>{t('logout.inspector.rotation.emptyHint')}</EmptyHint>
      ) : (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}
          role="list"
          aria-label={t('logout.inspector.rotation.chainAriaLabel')}
        >
          {chainNodes.map((node, idx) => (
            <React.Fragment key={node.jti}>
              <div
                role="listitem"
                style={{
                  background: 'var(--color-surface-elevated)',
                  border: `1px solid ${node.status === 'active' ? 'var(--color-success)' : 'var(--color-danger)'}`,
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  minWidth: '80px',
                  opacity: node.status === 'revoked' ? 0.7 : 1,
                }}
              >
                <span style={{ fontWeight: 700, color: node.status === 'active' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                  {node.jti}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
                  {node.status === 'active' ? 'active' : 'blacklisted'}
                </span>
              </div>
              {idx < chainNodes.length - 1 && (
                <span aria-hidden="true" style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-lg)' }}>
                  →
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inner page (hooks only called when authenticated) ────────────────────────

function InspectorPageContent() {
  const { t } = useTranslation();
  const [sessionData, setSessionData] = useState<{
    revokedItems: RevokedJti[];
    activeTokens: ActiveRefreshToken[];
  } | null>(null);

  const handleSessionData = useCallback(
    (data: { revokedItems: RevokedJti[]; activeTokens: ActiveRefreshToken[] } | null) => {
      setSessionData(data);
    },
    []
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-surface-background)', fontFamily: 'var(--font-family-sans)' }}>
      {/* Breadcrumb */}
      <nav
        aria-label={t('common.breadcrumb')}
        style={{
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
          padding: 'var(--spacing-3) var(--spacing-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <a href="/_index" style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }} aria-label={t('common.home')}>{t('common.home')}</a>
        <span style={{ color: 'var(--color-text-muted)' }} aria-hidden="true">›</span>
        <span>{t('logout.inspector.breadcrumb.devtools')}</span>
        <span style={{ color: 'var(--color-text-muted)' }} aria-hidden="true">›</span>
        <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }} aria-current="page">{t('logout.inspector.title')}</span>
        <span
          aria-label={t('logout.inspector.adminOnly')}
          style={{
            display: 'inline-block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'color-mix(in srgb, var(--color-danger) 15%, transparent)',
            color: 'var(--color-danger)',
            border: '1px solid var(--color-danger)',
            marginLeft: 'var(--spacing-2)',
          }}
        >
          ROLE_ADMIN
        </span>
      </nav>

      {/* Main */}
      <main style={{ padding: 'var(--spacing-6)', maxWidth: '1080px', margin: '0 auto' }} aria-label={t('logout.inspector.mainAriaLabel')}>
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-text-primary)', margin: '0 0 var(--spacing-6)' }}>
          {t('logout.inspector.title')}
        </h1>

        {/* 2-panel grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <JtiBlacklistPanel />
          <SessionsPanel onDataReady={handleSessionData} />
        </div>

        {/* Rotation chain */}
        <RotationPanel
          revokedItems={sessionData?.revokedItems ?? []}
          activeTokens={sessionData?.activeTokens ?? []}
        />

        {/* §6.7 Common Usage Panel */}
        <CommonUsagePanel
          items={COMMON_USAGE_ITEMS}
          defaultOpen={false}
          title={t('logout.commonUsage.title')}
        />
      </main>

      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (max-width: 767px) {
          .logout-inspector-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── LogoutInspectorPage ─────────────────────────────────────────────────────
// Auth/role enforcement is delegated to AdminRouteGuard in router.tsx.
// This component simply renders the page content.

export function LogoutInspectorPage() {
  return <InspectorPageContent />;
}
